import { API_BASE_URL } from '@/lib/solaApi';
import { parseReference, referenceToSlug } from '@/lib/bibleReference';

/**
 * Shared-answer pages (/bible-verse-explained/ask/[slug]) — the "share this
 * answer as a link" feature (Jay 2026-07-11, for Reddit distribution).
 *
 * Persistence strategy: NO database. Both upstream calls are cached in
 * Next's Data Cache via fetch `next.revalidate`, the exact mechanism the
 * Tier-2 verse pages already rely on for their POST LLM fetches (see
 * solaApi.ts). First visit to a slug generates; every visitor after —
 * i.e. an entire Reddit thread — gets the same stored answer with zero
 * upstream calls until the revalidate window lapses. The cache key
 * includes the request body, so each distinct question caches separately.
 *
 * The slug IS the query (hyphenated words), so links are self-describing
 * and nothing needs to be written at share time — sharing is just copying
 * a URL, and the share button warms the cache with a background fetch.
 */

// Cache indefinitely (Jay 2026-07-11): a shared link should read the same
// in month six as the day it was posted — revalidate windows would let the
// answer silently drift after regeneration (LLM non-determinism), which is
// wrong for a link quoted in a Reddit thread. Next serves stale-while-
// revalidate anyway, so even `false` never makes a visitor wait; the only
// regeneration path left is rare Data Cache eviction (self-heals with one
// ~2s skeleton visit) or a deliberate purge.
const REVALIDATE_ANSWER = false as const;

export const ASK_QUERY_MAX_CHARS = 120;

export function queryToAskSlug(query: string): string {
  return query
    .toLowerCase()
    .trim()
    .slice(0, ASK_QUERY_MAX_CHARS)
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function askSlugToQuery(slug: string): string | null {
  const cleaned = decodeURIComponent(slug)
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, ' ')
    .trim();
  if (cleaned.length < 3 || cleaned.length > ASK_QUERY_MAX_CHARS) return null;
  return cleaned;
}

/** Collect the upstream SSE stream into the full markdown answer. */
export async function fetchSharedAnswer(query: string): Promise<string | null> {
  const res = await fetch(`${API_BASE_URL}/api/search/description`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
    next: { revalidate: REVALIDATE_ANSWER },
  });
  if (!res.ok) return null;
  const raw = await res.text();
  let answer = '';
  for (const line of raw.split('\n')) {
    const m = line.match(/^data:\s*(.+)$/);
    if (!m) continue;
    try {
      const payload = JSON.parse(m[1]);
      if (typeof payload.chunk === 'string') answer += payload.chunk;
    } catch {
      /* keepalive lines */
    }
  }
  return answer.trim().length > 0 ? answer : null;
}

export interface SharedPassage {
  reference: string;
  text: string;
  relevance: string;
  href: string | null;
}

export async function fetchSharedPassages(query: string): Promise<SharedPassage[]> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/search/verses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, limit: 5 }),
      next: { revalidate: REVALIDATE_ANSWER },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as {
      data?: { verses?: { reference: string; text: string; relevance: string }[] };
    };
    return (json.data?.verses ?? [])
      .map((v) => {
        const parsed = parseReference(v.reference);
        return {
          reference: v.reference,
          text: v.text,
          relevance: v.relevance,
          href: parsed.ok ? `/bible-verse-explained/${referenceToSlug(parsed.reference)}` : null,
        };
      })
      .filter((p) => p.text && p.reference);
  } catch {
    return [];
  }
}
